"use client";

import { useEffect, useState } from "react";
import {
  Modal,
  Text,
  Button,
  Stack,
  Title,
  Anchor,
  FocusTrap,
} from "@mantine/core";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { authenticatedFetch } from "@/app/_utils/authenticatedFetch";
import { useUserContext } from "@/app/_hooks/useUserContext";
import { useSetAlert } from "@/app/_hooks/useAlertContext";
import {
  useIsSubscriptionReady,
  useRefreshSubscription,
  useSubscriptionContext,
} from "@/app/_hooks/subscription/useSubscriptionContext";
import {
  BLOCK_AFTER_ATTEMPTS,
  billingStageFor,
} from "@/app/_constants/subscriptionBilling";

dayjs.extend(utc);
dayjs.extend(timezone);

const SUPPORT_EMAIL = "contact@mountmonitor.com";

// Thresholds live in _constants/subscriptionBilling so the email the webhook
// sends and this modal agree. A dismissal only lasts for that attempt; the
// next failed retry brings the modal back.

const formatAmount = (amountMinor: number, currency: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amountMinor / 100);

// TEMP PREVIEW: fake failure so the modal shows on every page in dev.
// 1 = nothing (grace), 2 = notice, 3 = warning, 4 = block. Set to 0 (or
// delete) when done.
const MOCK_ATTEMPT = 0;

const readMockBilling = (): SubscriptionBilling | null => {
  if (process.env.NODE_ENV !== "development") return null;
  const attempt = MOCK_ATTEMPT;
  if (!attempt) return null;
  const day = 24 * 60 * 60 * 1000;
  return {
    status: "past_due",
    invoiceId: "in_mock",
    attemptCount: attempt,
    nextAttemptAt:
      attempt >= BLOCK_AFTER_ATTEMPTS ? null : Date.now() + 2 * day,
    hostedInvoiceUrl: "https://invoice.stripe.com/i/mock",
    amountDue: 19999,
    currency: "usd",
    updatedAt: Date.now() - 2 * day,
  };
};

// Driven entirely by users/{uid}/metadata/subscription.billing, which the
// billing webhook maintains from invoice.payment_failed / invoice.paid. To
// block a tenant by hand, set that field with attemptCount >= 4 and a
// hostedInvoiceUrl; to lift it, set status to "ok" or delete the field.
export const BillingOverdueModal = () => {
  const user = useUserContext();
  const setAlert = useSetAlert();
  const subscription = useSubscriptionContext();
  const isSubscriptionReady = useIsSubscriptionReady();
  const refreshSubscription = useRefreshSubscription();
  // Hidden until localStorage has been checked, so a dismissed notice
  // doesn't flash on load.
  const [dismissed, setDismissed] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  // Read in an effect, not during render, so SSR and client markup agree.
  const [mockBilling, setMockBilling] = useState<SubscriptionBilling | null>(
    null
  );
  useEffect(() => setMockBilling(readMockBilling()), []);

  const billing = mockBilling ?? subscription?.billing;
  const isPastDue =
    (isSubscriptionReady || !!mockBilling) && billing?.status === "past_due";
  const billingStage = isPastDue
    ? billingStageFor(billing.attemptCount, billing.nextAttemptAt)
    : "none";
  const isShown = billingStage !== "none";
  const isBlocked = billingStage === "blocked";
  const isWarning = billingStage === "warning";
  const dismissKey = isShown
    ? `mm_billing_notice_${billing.invoiceId}_${billing.attemptCount}`
    : null;

  useEffect(() => {
    if (!dismissKey) return;
    try {
      setDismissed(localStorage.getItem(dismissKey) === "true");
    } catch {
      setDismissed(false);
    }
  }, [dismissKey]);

  // Support gets an email the first time a given attempt's modal is seen and
  // on every interaction, so a customer trying to sort it out is visible
  // before they get blocked. Fire and forget, except when navigating away.
  const notify = (action: string, detail?: string) =>
    authenticatedFetch("/api/email/billingOverdueAttempt", {
      method: "POST",
      body: JSON.stringify({
        userName: user?.settings?.name || user?.details?.fullName || "Unknown",
        userId: user?.uid,
        action,
        detail: detail ?? null,
        stage: billingStage,
        attemptCount: billing?.attemptCount ?? null,
        amount: billing
          ? formatAmount(billing.amountDue, billing.currency)
          : null,
        invoiceId: billing?.invoiceId ?? null,
        daysOverdue: billing
          ? Math.max(0, dayjs().diff(dayjs(billing.updatedAt), "day"))
          : null,
      }),
    }).catch((err) =>
      console.error("Failed to notify billing overdue action:", err)
    );

  useEffect(() => {
    const visible = isShown && (isBlocked || !dismissed) && !mockBilling;
    if (!visible) return;
    const seenKey = `mm_billing_seen_${billing.invoiceId}_${billing.attemptCount}`;
    try {
      if (localStorage.getItem(seenKey) === "true") return;
      localStorage.setItem(seenKey, "true");
    } catch {
      // No storage means no dedupe, and emailing on every load is worse
      // than missing this one.
      return;
    }
    notify("shown");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShown, isBlocked, dismissed, mockBilling]);

  if (!isShown) return null;
  if (!isBlocked && dismissed && !mockBilling) return null;

  const tz = user?.settings?.timezone || dayjs.tz.guess();
  const amount = formatAmount(billing.amountDue, billing.currency);
  const nextAttempt = billing.nextAttemptAt
    ? dayjs(billing.nextAttemptAt).tz(tz).format("dddd, MMMM D")
    : null;
  const attempts =
    billing.attemptCount === 1 ? "once" : `${billing.attemptCount}`;
  const dismiss = () => {
    try {
      localStorage.setItem(dismissKey!, "true");
    } catch {
      // Storage unavailable; the notice simply shows again next load.
    }
    setDismissed(true);
    notify("dismissed");
  };

  const handleUpdatePaymentMethod = async () => {
    setIsRedirecting(true);
    try {
      const res = await authenticatedFetch(
        "/api/subscription/update-payment-method",
        { method: "POST" }
      );
      const data = await res.json();
      if (!res.ok || !data.url) {
        throw new Error(data.error || "Failed to create session");
      }

      // Awaited: navigating away would cancel it otherwise.
      await notify("update_payment_method");

      window.location.assign(data.url);
    } catch (error) {
      console.error("Failed to start payment method update:", error);
      setIsRedirecting(false);
      setAlert({
        type: "error",
        title: "Unable to open payment update",
        content: `Please try again, or contact us at ${SUPPORT_EMAIL}.`,
      });
    }
  };

  // Asks Stripe to collect the invoice with the card on file now (or confirms
  // it is already paid), then refetches so the block lifts without a reload.
  const handleCheckAgain = async () => {
    setIsChecking(true);
    try {
      const res = await authenticatedFetch("/api/subscription/retry-invoice", {
        method: "POST",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Retry failed");
      notify(
        "retry_payment",
        data.status === "ok" ? "Collected, block lifted" : data.message
      );
      if (data.status === "failed") {
        setAlert({
          type: "error",
          title: "Payment not collected",
          content: data.message,
        });
      }
      await refreshSubscription();
    } catch (error) {
      console.error("Failed to retry subscription payment:", error);
      setAlert({
        type: "error",
        title: "Unable to retry payment",
        content: `Please try again, or contact us at ${SUPPORT_EMAIL}.`,
      });
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={isBlocked ? () => {} : dismiss}
      transitionProps={{ transition: "fade", duration: 300 }}
      title={
        <Title order={3} fw={700}>
          Subscription Overdue
        </Title>
      }
      closeOnClickOutside={!isBlocked}
      closeOnEscape={!isBlocked}
      withCloseButton={!isBlocked}
      overlayProps={{
        backgroundOpacity: 0.35,
        style: {
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        },
      }}
      centered
      size="md"
    >
      <FocusTrap.InitialFocus />
      <Stack gap="md">
        {isBlocked ? (
          <>
            <Text>
              We have made {attempts} unsuccessful attempts to process your
              subscription payment.
            </Text>
            <Text>Access to MountMonitor is currently paused.</Text>
            <Text>
              Please Pay the invoice below, or update your card and then hit
              Retry Payment Now. Either way you will be back in right after.
            </Text>
          </>
        ) : (
          <>
            {attempts.toString() === "2" ? (
              <Text>We were unable to process your subscription payment.</Text>
            ) : (
              <Text>
                We&apos;ve been unable to process your subscription payment a
                few times.
              </Text>
            )}
            <Text>
              {nextAttempt ? (
                <>
                  We will automatically try again on{" "}
                  <Text span fw={600}>
                    {nextAttempt}
                  </Text>
                  .{" "}
                </>
              ) : null}
            </Text>
            <Text>
              If your payment method has changed or expired, please update it
              below and we will take care of the rest.
            </Text>
            {isWarning && (
              <Text>
                <Text span fw={600}>
                  Just a heads up:
                </Text>{" "}
                if the next attempt does not go through, access to MountMonitor
                will be paused until the subscription is paid.
              </Text>
            )}
          </>
        )}

        <Stack gap="xs" mt="xs">
          {/* Blocked: paying the invoice is the way out, so it leads.
              Notices: the retry will run anyway, so the payment method
              link leads and the invoice is the optional shortcut. */}
          {isBlocked && billing.hostedInvoiceUrl && (
            <Button
              component="a"
              href={billing.hostedInvoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => notify("pay_invoice")}
              fullWidth
            >
              Pay Invoice
            </Button>
          )}
          <Button
            variant={
              isBlocked && billing.hostedInvoiceUrl ? "default" : "filled"
            }
            onClick={handleUpdatePaymentMethod}
            loading={isRedirecting}
            fullWidth
          >
            Update Payment Method
          </Button>
          {!isBlocked && billing.hostedInvoiceUrl && (
            <Button
              component="a"
              href={billing.hostedInvoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="default"
              onClick={() => notify("pay_invoice")}
              fullWidth
            >
              Pay Invoice Now
            </Button>
          )}
          {isBlocked ? (
            <Button
              variant="subtle"
              color="gray"
              onClick={handleCheckAgain}
              loading={isChecking}
              fullWidth
            >
              Retry Payment Now
            </Button>
          ) : null}
        </Stack>

        <Text size="sm">
          Need a hand? Email{" "}
          <Anchor
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
              "Resolve overdue subscription"
            )}`}
            fw={600}
          >
            {SUPPORT_EMAIL}
          </Anchor>
          .
        </Text>
      </Stack>
    </Modal>
  );
};
